import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

export type TenantPortalDashboard = {
  organization?: TenantPortalOrganization | null;
  tenant?: TenantPortalTenant | null;
  lease?: TenantPortalLease | null;
  leaseOptions?: TenantPortalLeaseOption[] | null;
  balance?: TenantBalanceSummary | null;
  nextCharge?: TenantRentCharge | null;
  recentCharges?: TenantRentCharge[] | null;
  paymentSchedule?: TenantRentCharge[] | null;
  leaseTimeline?: TenantLeaseTimelineItem[] | null;
  actionItems?: TenantActionItem[] | null;
  moveInChecklist?: TenantChecklistItem[] | null;
  maintenanceRequests?: TenantMaintenanceRequest[] | null;
  notices?: TenantNotice[] | null;
  messages?: TenantMessage[] | null;
  documents?: TenantDocument[] | null;
};

export type TenantPortalOrganization = {
  id?: string | null;
  name?: string | null;
  logoUrl?: string | null;
  coverImageUrl?: string | null;
  primaryColor?: string | null;
  currency?: string | null;
  timeZone?: string | null;
};

export type TenantPortalTenant = {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  profileImageUrl?: string | null;
  /** The name on the tenant's verified ID; when set, leases must be signed with exactly this name. */
  verifiedLegalName?: string | null;
};

export type TenantPortalLease = {
  id?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  rent?: number | string | null;
  propertyName?: string | null;
  propertyAddress?: string | null;
  propertyId?: string | null;
  propertyImageUrl?: string | null;
  coverImageUrl?: string | null;
  imageUrl?: string | null;
  unitId?: string | null;
  unitName?: string | null;
  leaseType?: string | null;
  tenantSummary?: string | null;
  signedDate?: string | null;
  propertyManagerName?: string | null;
  rentFrequency?: string | null;
  rentDueDay?: number | null;
  gracePeriodDays?: number | null;
  lateFeePolicy?: string | null;
  renewalWindowDate?: string | null;
  signatureRequired?: boolean | null;
  readyForSignature?: boolean | null;
  signed?: boolean | null;
  signableDocumentId?: string | null;
  signableDocumentUrl?: string | null;
  signatureStatus?: string | null;
  /** This tenant signed the current version; co-tenants may still need to. */
  signedByYou?: boolean | null;
  tenantSignaturesCollected?: number | null;
  tenantSignaturesRequired?: number | null;
  /** Continuing a month at a time after its original end date (optional until the API sends it). */
  monthToMonth?: boolean | null;
};

export type TenantPortalLeaseOption = {
  orgId?: string | null;
  orgName?: string | null;
  leaseId?: string | null;
  propertyId?: string | null;
  propertyName?: string | null;
  propertyAddress?: string | null;
  propertyImageUrl?: string | null;
  propertyLogoUrl?: string | null;
  coverImageUrl?: string | null;
  imageUrl?: string | null;
  unitId?: string | null;
  unitName?: string | null;
  status?: string | null;
  role?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  rent?: number | string | null;
  currentBalance?: number | string | null;
  overdueBalance?: number | string | null;
  nextDueDate?: string | null;
  selected?: boolean | null;
};

export type TenantBalanceSummary = {
  totalDue?: number | string | null;
  pastDueAmount?: number | string | null;
  nextRentAmount?: number | string | null;
  nextRentDueDate?: string | null;
  credits?: number | string | null;
  lastPaymentAmount?: number | string | null;
  lastPaymentDate?: string | null;
  runningBalance?: number | string | null;
};

export type TenantRentCharge = {
  id?: string | null;
  dueDate?: string | null;
  amount?: number | string | null;
  paidAmount?: number | string | null;
  balance?: number | string | null;
  chargeType?: string | null;
  status?: string | null;
};

export type TenantActionItem = {
  id?: string | null;
  type?: string | null;
  title?: string | null;
  description?: string | null;
  detail?: string | null;
  amount?: number | string | null;
  dueDate?: string | null;
  status?: string | null;
  actionLabel?: string | null;
  actionUrl?: string | null;
};

export type TenantLeaseTimelineItem = {
  title?: string | null;
  description?: string | null;
  date?: string | null;
  tone?: 'complete' | 'info' | 'upcoming' | string | null;
  actionLabel?: string | null;
  actionUrl?: string | null;
};

export type TenantChecklistItem = {
  id?: string | null;
  title?: string | null;
  status?: string | null;
  completed?: boolean | null;
  dueDate?: string | null;
};

export type TenantMaintenanceRequest = {
  id?: string | null;
  title?: string | null;
  status?: string | null;
  updatedAt?: string | null;
  category?: string | null;
};

export type TenantNotice = {
  id?: string | null;
  title?: string | null;
  body?: string | null;
  publishedAt?: string | null;
  imageUrl?: string | null;
  actionUrl?: string | null;
  read?: boolean | null;
};

export type TenantMessage = {
  id?: string | null;
  senderName?: string | null;
  category?: string | null;
  preview?: string | null;
  sentAt?: string | null;
};

export type TenantDocument = {
  id?: string | null;
  title?: string | null;
  description?: string | null;
  category?: string | null;
  status?: string | null;
  fileType?: string | null;
  downloadUrl?: string | null;
  fileUrl?: string | null;
  originalFilename?: string | null;
  sizeBytes?: number | null;
  files?: TenantDocumentFile[] | null;
  propertyId?: string | null;
  propertyName?: string | null;
  unitId?: string | null;
  unitNumber?: string | null;
  tenantId?: string | null;
  tenantName?: string | null;
  leaseId?: string | null;
  submittedBy?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  uploadedAt?: string | null;
  lastModifiedAt?: string | null;
  lastModifiedDate?: string | null;
};

export type TenantDocumentFile = {
  id?: string | null;
  fileUrl?: string | null;
  downloadUrl?: string | null;
  publicId?: string | null;
  originalFilename?: string | null;
  contentType?: string | null;
  fileExtension?: string | null;
  sizeBytes?: number | null;
  sortOrder?: number | null;
  uploadedAt?: string | null;
};

export type TenantRentPayment = {
  receiptNumber?: string | null;
  id?: string | null;
  leaseId?: string | null;
  scheduleId?: string | null;
  tenantId?: string | null;
  amount?: number | string | null;
  applied?: number | string | null;
  remaining?: number | string | null;
  reversedAmount?: number | string | null;
  reversibleAmount?: number | string | null;
  currency?: string | null;
  status?: string | null;
  type?: string | null;
  origin?: string | null;
  paymentAttemptId?: string | null;
  reference?: string | null;
  notes?: string | null;
  externalId?: string | null;
  externalSource?: string | null;
  gatewayStatus?: string | null;
  receivedAt?: string | null;
  createdDate?: string | null;
  lastModifiedDate?: string | null;
  /** Apex's separate convenience fee for this online payment; not part of `amount`. */
  convenienceFee?: TenantPaymentConvenienceFee | null;
};

export type TenantPaymentConvenienceFee = {
  amount: number;
  currency?: string | null;
  basis?: string | null;
  status: 'PENDING' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | 'UNKNOWN' | 'REFUNDED' | string;
};

export type ReceiptEmailDelivery = {
  status: string;
  attempts: number;
  sentAt?: string | null;
  message: string;
  canResend: boolean;
};

export type TenantPaymentReceipt = {
  emailDelivery?: ReceiptEmailDelivery | null;
  payment?: TenantRentPayment | null;
  allocations?: Array<{
    scheduleId?: string | null;
    chargeType?: string | null;
    dueDate?: string | null;
    amount?: number | string | null;
    applied?: number | string | null;
    entryType?: string | null;
  }> | null;
  files?: Array<{
    id?: string | null;
    url?: string | null;
    originalFilename?: string | null;
    label?: string | null;
    contentType?: string | null;
    generatedReceipt?: boolean;
    title?: string | null;
    fileType?: string | null;
    downloadUrl?: string | null;
  }> | null;
};

export type TenantPaymentPlatformStatus = {
  orgId?: string | null;
  provider?: string | null;
  status?: string | null;
  accountCreated?: boolean | null;
  chargesEnabled?: boolean | null;
  payoutsEnabled?: boolean | null;
  paymentsReady?: boolean | null;
  publishableKey?: string | null;
  stripeAccountId?: string | null;
  providerAccountId?: string | null;
  disabledReason?: string | null;
  convenienceFee?: TenantConvenienceFee | null;
};

export type TenantPaymentMethodPreference = 'ACH' | 'DEBIT_CARD' | 'CREDIT_CARD' | 'OFFLINE';

/** Apex's separate convenience fee for paying online (AppFolio-style), as it applies to this tenant. */
export type TenantConvenienceFee = {
  enabled: boolean;
  tenantPays: boolean;
  creditPercent: number;
  creditFixed: number;
  debitBands: { upTo: number | null; fee: number }[];
  achFixed: number;
  refundPolicy: 'NON_REFUNDABLE_EXCEPT_PLATFORM_ERROR' | 'REFUND_WITH_RENT' | string;
};

export type TenantPlatformMethodKind = 'CARD' | 'BANK_ACCOUNT';

export type TenantPlatformSetup = { clientSecret: string; setupIntentId: string; publishableKey: string; customerId: string };

export type TenantPlatformSavedMethod = {
  paymentMethodId: string;
  method: Exclude<TenantPaymentMethodPreference, 'OFFLINE'>;
  brand?: string | null;
  last4?: string | null;
  bankName?: string | null;
  amount: number;
  convenienceFee: number;
  feeBasis: string;
  total: number;
  currency: string;
  tenantPaysFee: boolean;
};

export type TenantPlatformCheckoutResponse = {
  paymentAttemptId: string;
  status: 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | 'REQUIRES_REVIEW' | string;
  providerStatus?: string | null;
  amount: number;
  convenienceFee: number;
  feeBasis?: string | null;
  total: number;
  currency: string;
  feeStatus?: string | null;
  clientSecret?: string | null;
  publishableKey?: string | null;
  connectedAccountId?: string | null;
  failureReason?: string | null;
};

export type TenantPaymentSettings = {
  orgId?: string | null;
  enabledMethods?: TenantPaymentMethodPreference[] | null;
  preferredMethod?: TenantPaymentMethodPreference | null;
  feeHandling?: string | null;
  requireFeeFreeOption?: boolean | null;
  allowPartialPayments?: boolean | null;
  allowAdvancePayments?: boolean | null;
  allowCreditCard?: boolean | null;
  allowDebitCard?: boolean | null;
  achFeeWaived?: boolean | null;
  platformFeeAmount?: number | null;
};

export type TenantPaymentOptionsResponse = TenantPaymentSettings & TenantPaymentPlatformStatus;

export type TenantPaymentIntentType =
  | 'RENT'
  | 'LATE_FEE'
  | 'SECURITY_DEPOSIT'
  | 'OTHER_PROPERTY_FEE';

export type TenantCreatePaymentIntentRequest = {
  advancePayment?: boolean;
  idempotencyKey: string;
  orgId: string;
  leaseId: string;
  scheduleId?: string | null;
  amount: number;
  currency: string;
  provider: 'STRIPE';
  paymentMethod: Exclude<TenantPaymentMethodPreference, 'OFFLINE'>;
  intentType: TenantPaymentIntentType;
  userId?: string | null;
  description?: string | null;
  externalReference?: string | null;
};

export type TenantCreatePaymentIntentResponse = {
  clientSecret?: string | null;
  client_secret?: string | null;
  paymentAttemptId?: string | null;
  amount?: number | string | null;
  platformFeeAmount?: number | string | null;
  chargedAmount?: number | string | null;
  tenantFeeAmount?: number | string | null;
  tenantPlatformFeeAmount?: number | string | null;
  processingFeeAmount?: number | string | null;
  serverConfirmationRequired?: boolean;
  publishableKey?: string | null;
  publishable_key?: string | null;
  stripePublishableKey?: string | null;
  stripeAccountId?: string | null;
  stripe_account_id?: string | null;
  providerAccountId?: string | null;
};

export type TenantConfirmPaymentIntentRequest = {
  orgId: string;
  leaseId: string;
  paymentAttemptId: string;
  providerPaymentId?: string | null;
};

export type TenantConfirmPaymentIntentResponse = {
  paymentAttemptId?: string | null;
  providerPaymentId?: string | null;
  providerStatus?: string | null;
  status?: 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | string | null;
};

export type TenantAutoPayCadence = 'MONTHLY_DUE_DATE' | 'WEEKLY' | 'BIWEEKLY';
export type TenantAutoPayStatus = 'ACTIVE' | 'PAUSED' | 'DISABLED' | 'SETUP_REQUIRED';
export type TenantAutoPayDayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export type TenantAutoPaySetupIntentRequest = {
  paymentMethod?: Exclude<TenantPaymentMethodPreference, 'OFFLINE'> | null;
  currency?: string | null;
};

export type TenantAutoPaySetupIntentResponse = {
  feeDisclosure?: string | null;
  clientSecret?: string | null;
  setupIntentId?: string | null;
  publishableKey?: string | null;
  stripeAccountId?: string | null;
  providerCustomerId?: string | null;
  orgId?: string | null;
  leaseId?: string | null;
  tenantId?: string | null;
};

export type TenantAutoPayCompleteSetupRequest = {
  setupIntentId: string;
  paymentMethod?: Exclude<TenantPaymentMethodPreference, 'OFFLINE'> | null;
  cadence?: TenantAutoPayCadence | null;
  installmentAmount?: number | null;
  maxAmount?: number | null;
  chargeDayOfWeek?: TenantAutoPayDayOfWeek | null;
  chargeDaysBeforeDue?: number | null;
  nextRunDate?: string | null;
  enabled?: boolean | null;
  currency?: string | null;
};

/** Auto-pay with a card or bank account saved on the Apex platform (convenience-fee checkout). */
export type TenantAutoPayPlatformEnrollRequest = Omit<TenantAutoPayCompleteSetupRequest, 'setupIntentId' | 'paymentMethod'> & {
  platformPaymentMethodId: string;
};

export type TenantAutoPayUpdateRequest = {
  enabled?: boolean | null;
  cadence?: TenantAutoPayCadence | null;
  installmentAmount?: number | null;
  maxAmount?: number | null;
  chargeDayOfWeek?: TenantAutoPayDayOfWeek | null;
  chargeDaysBeforeDue?: number | null;
  nextRunDate?: string | null;
};

export type TenantAutoPayEnrollment = {
  id?: string | null;
  orgId?: string | null;
  leaseId?: string | null;
  tenantId?: string | null;
  status?: TenantAutoPayStatus | string | null;
  cadence?: TenantAutoPayCadence | string | null;
  installmentAmount?: number | string | null;
  chargeDayOfWeek?: TenantAutoPayDayOfWeek | string | null;
  chargeDaysBeforeDue?: number | null;
  nextRunDate?: string | null;
  maxAmount?: number | string | null;
  currency?: string | null;
  paymentMethod?: {
    id?: string | null;
    methodType?: string | null;
    brand?: string | null;
    last4?: string | null;
    expMonth?: number | null;
    expYear?: number | null;
    bankName?: string | null;
  } | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastRunAt?: string | null;
  failedAttemptCount?: number | null;
  lastFailureReason?: string | null;
  retryAfterDate?: string | null;
};

export type TenantPaymentProofStatus = 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export type TenantPaymentProof = {
  id?: string | null;
  organizationId?: string | null;
  leaseId?: string | null;
  tenantId?: string | null;
  scheduleId?: string | null;
  paymentId?: string | null;
  documentId?: string | null;
  amount?: number | string | null;
  currency?: string | null;
  reference?: string | null;
  notes?: string | null;
  status?: TenantPaymentProofStatus | null;
  fileUrl?: string | null;
  publicId?: string | null;
  contentType?: string | null;
  originalFilename?: string | null;
  sizeBytes?: number | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  createdDate?: string | null;
  lastModifiedDate?: string | null;
};

export type TenantPage<T> = {
  content?: T[] | null;
  totalElements?: number | null;
  totalPages?: number | null;
  number?: number | null;
  size?: number | null;
};

export type OrganizationContextItem = {
  orgId: string;
  orgName: string;
  portalSlug?: string | null;
  orgType?: string | null;
  orgRole?: string | null;
  portalMode?: string | null;
  landingPath?: string | null;
  assignedPropertyIds?: string[] | null;
  owner?: boolean | null;
  subscriptionActive?: boolean | null;
  permissions?: string[] | null;
};

export type OrganizationContextResponse = {
  hasMembership?: boolean;
  defaultOrgId?: string | null;
  organizations?: OrganizationContextItem[] | null;
};

@Injectable({ providedIn: 'root' })
export class TenantPortalService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiGatewayUrl}/property-service`;
  private readonly paymentsBase = `${environment.apiGatewayUrl}/payments/api/v1`;

  getOrganizationContext(): Observable<OrganizationContextResponse> {
    return this.http.get<OrganizationContextResponse>(`${this.base}/organization/context`, {
      withCredentials: true,
    });
  }

  dashboard(orgId?: string | null, leaseId?: string | null, unitId?: string | null): Observable<TenantPortalDashboard> {
    let params = new HttpParams();
    if (orgId?.trim()) {
      params = params.set('orgId', orgId.trim());
    }
    if (leaseId?.trim()) {
      params = params.set('leaseId', leaseId.trim());
    }
    if (unitId?.trim()) {
      params = params.set('unitId', unitId.trim());
    }
    return this.http.get<TenantPortalDashboard>(`${this.base}/tenant-portal/me/dashboard`, {
      params,
      withCredentials: true,
    });
  }

  leases(orgId?: string | null, leaseId?: string | null, unitId?: string | null): Observable<TenantPortalLeaseOption[]> {
    let params = new HttpParams();
    if (orgId?.trim()) {
      params = params.set('orgId', orgId.trim());
    }
    if (leaseId?.trim()) {
      params = params.set('leaseId', leaseId.trim());
    }
    if (unitId?.trim()) {
      params = params.set('unitId', unitId.trim());
    }

    return this.http.get<TenantPortalLeaseOption[]>(`${this.base}/tenant-portal/me/leases`, {
      params,
      withCredentials: true,
    });
  }

  browseDocuments(orgId: string, leaseId: string | null, unitId: string | null,
                  filters: { page: number; size: number; q: string; category: string; status: string; from?: string; to?: string }) {
    let params = new HttpParams().set('orgId', orgId);
    if (leaseId) params = params.set('leaseId', leaseId);
    if (unitId) params = params.set('unitId', unitId);
    for (const [key, value] of Object.entries(filters)) if (value !== undefined) params = params.set(key, value);
    return this.http.get<{ content: TenantDocument[]; totalElements: number; number: number; counts: Record<string, number> }>(
      `${this.base}/tenant-portal/me/documents/browse`, { params, withCredentials: true });
  }

  documents(orgId?: string | null, leaseId?: string | null, unitId?: string | null, size = 100): Observable<TenantDocument[]> {
    let params = new HttpParams().set('size', String(size));
    if (orgId?.trim()) {
      params = params.set('orgId', orgId.trim());
    }
    if (leaseId?.trim()) {
      params = params.set('leaseId', leaseId.trim());
    }
    if (unitId?.trim()) {
      params = params.set('unitId', unitId.trim());
    }

    return this.http.get<TenantDocument[]>(`${this.base}/tenant-portal/me/documents`, {
      params,
      withCredentials: true,
    });
  }

  /** The itemized security deposit statement for a lease, or null until staff have sent it. */
  depositStatement(orgId: string, leaseId: string): Observable<TenantDepositStatement | null> {
    return this.http.get<TenantDepositStatement | null>(
      `${this.base}/tenant-portal/me/leases/${encodeURIComponent(leaseId)}/deposit-settlement`,
      { params: { orgId }, withCredentials: true });
  }

  /** Whether the tenant can ask to renew this lease now, the lengths on offer, their latest request and any open offer. */
  renewalStatus(orgId: string, leaseId: string): Observable<TenantRenewalStatus> {
    return this.http.get<TenantRenewalStatus>(`${this.base}/tenant-portal/me/leases/${leaseId}/renewal`, {
      params: new HttpParams().set('orgId', orgId),
      withCredentials: true,
    });
  }

  requestRenewal(orgId: string, leaseId: string, termMonths: number, note: string | null): Observable<TenantRenewalRequest> {
    return this.http.post<TenantRenewalRequest>(`${this.base}/tenant-portal/me/leases/${leaseId}/renewal/requests`, { termMonths, note }, {
      params: new HttpParams().set('orgId', orgId),
      withCredentials: true,
    });
  }

  withdrawRenewalRequest(orgId: string, leaseId: string, requestId: string): Observable<TenantRenewalRequest> {
    return this.http.post<TenantRenewalRequest>(
      `${this.base}/tenant-portal/me/leases/${leaseId}/renewal/requests/${requestId}/withdraw`, null, {
        params: new HttpParams().set('orgId', orgId),
        withCredentials: true,
      });
  }

  declineRenewalOffer(orgId: string, leaseId: string, reason: string | null): Observable<TenantRenewalStatus> {
    return this.http.post<TenantRenewalStatus>(`${this.base}/tenant-portal/me/leases/${leaseId}/renewal/offer/decline`, { reason }, {
      params: new HttpParams().set('orgId', orgId),
      withCredentials: true,
    });
  }

  signLease(request: {
    orgId: string;
    leaseId: string;
    unitId?: string | null;
    signatureName: string;
    initials: string;
    accepted: boolean;
  }): Observable<TenantPortalDashboard> {
    let params = new HttpParams()
      .set('orgId', request.orgId)
      .set('signatureName', request.signatureName)
      .set('initials', request.initials)
      .set('accepted', String(request.accepted));
    if (request.unitId?.trim()) {
      params = params.set('unitId', request.unitId.trim());
    }

    return this.http.post<TenantPortalDashboard>(
      `${this.base}/tenant-portal/me/leases/${request.leaseId}/sign`,
      null,
      {
        params,
        withCredentials: true,
      },
    );
  }

  uploadDocument(request: {
    orgId: string;
    leaseId?: string | null;
    unitId?: string | null;
    title?: string | null;
    description?: string | null;
    category?: string | null;
    file?: File | null;
    files?: File[] | null;
  }): Observable<TenantDocument> {
    const form = this.documentFormData(request);
    return this.http.post<TenantDocument>(`${this.base}/tenant-portal/me/documents`, form, {
      withCredentials: true,
    });
  }

  reuploadDocument(documentId: string, request: {
    orgId: string;
    leaseId?: string | null;
    unitId?: string | null;
    title?: string | null;
    description?: string | null;
    file?: File | null;
    files?: File[] | null;
  }): Observable<TenantDocument> {
    const form = this.documentFormData(request);
    return this.http.post<TenantDocument>(`${this.base}/tenant-portal/me/documents/${documentId}/replacement`, form, {
      withCredentials: true,
    });
  }

  rentPayments(orgId: string, leaseId: string, page = 0, size = 20): Observable<TenantPage<TenantRentPayment>> {
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    // The authenticated tenant is resolved by the server; never accept a payer ID from the browser.
    return this.http.get<TenantPage<TenantRentPayment>>(
      `${this.base}/tenant-portal/me/organizations/${orgId}/leases/${leaseId}/receipts`,
      { params, withCredentials: true },
    );
  }

  paymentReceipt(orgId: string, leaseId: string, paymentId: string): Observable<TenantPaymentReceipt> {
    return this.http.get<TenantPaymentReceipt>(
      `${this.base}/tenant-portal/me/organizations/${orgId}/leases/${leaseId}/receipts/${paymentId}`,
      { withCredentials: true },
    );
  }

  emailPaymentReceipt(orgId: string, leaseId: string, paymentId: string): Observable<void> {
    return this.http.post<void>(
      `${this.base}/tenant-portal/me/organizations/${orgId}/leases/${leaseId}/receipts/${paymentId}/email`,
      {},
      { withCredentials: true },
    );
  }

  paymentPlatformStatus(orgId: string): Observable<TenantPaymentPlatformStatus> {
    return this.http.get<TenantPaymentPlatformStatus>(`${this.paymentsBase}/payments/onboarding/status`, {
      params: { orgId },
      withCredentials: true,
    });
  }

  paymentSettings(orgId: string): Observable<TenantPaymentSettings> {
    return this.http.get<TenantPaymentSettings>(`${this.paymentsBase}/payments/settings`, {
      params: { orgId },
      withCredentials: true,
    });
  }

  tenantPaymentOptions(orgId: string, leaseId?: string | null): Observable<TenantPaymentOptionsResponse> {
    return this.http.get<TenantPaymentOptionsResponse>(`${this.base}/tenant-portal/me/payments/tenant/options`, {
      params: leaseId ? { orgId, leaseId } : { orgId },
      withCredentials: true,
    });
  }

  createPaymentIntent(request: TenantCreatePaymentIntentRequest): Observable<TenantCreatePaymentIntentResponse> {
    return this.http.post<TenantCreatePaymentIntentResponse>(
      `${this.base}/tenant-portal/me/payments/intent`,
      request,
      { withCredentials: true },
    );
  }

  platformPaymentMethodSetup(orgId: string, leaseId: string, kind: TenantPlatformMethodKind): Observable<TenantPlatformSetup> {
    return this.http.post<TenantPlatformSetup>(`${this.base}/tenant-portal/me/platform-payment-methods/setup`, { kind }, {
      params: new HttpParams().set('orgId', orgId).set('leaseId', leaseId),
      withCredentials: true,
    });
  }

  platformPaymentMethodComplete(orgId: string, leaseId: string, setupIntentId: string, amount: number): Observable<TenantPlatformSavedMethod> {
    return this.http.post<TenantPlatformSavedMethod>(`${this.base}/tenant-portal/me/platform-payment-methods/complete`,
      { setupIntentId, amount }, { params: new HttpParams().set('orgId', orgId).set('leaseId', leaseId), withCredentials: true });
  }

  platformCheckout(platformPaymentMethodId: string, payment: TenantCreatePaymentIntentRequest): Observable<TenantPlatformCheckoutResponse> {
    return this.http.post<TenantPlatformCheckoutResponse>(`${this.base}/tenant-portal/me/platform-checkout`,
      { platformPaymentMethodId, payment }, { withCredentials: true });
  }

  confirmPaymentIntent(request: TenantConfirmPaymentIntentRequest): Observable<TenantConfirmPaymentIntentResponse> {
    return this.http.post<TenantConfirmPaymentIntentResponse>(
      `${this.base}/tenant-portal/me/payments/intent/confirm`,
      request,
      { withCredentials: true },
    );
  }

  authorizeCard(request: { orgId: string; leaseId: string; paymentAttemptId: string; paymentMethodId: string | null }):
    Observable<{ id: string; status: string; clientSecret: string }> {
    return this.http.post<{ id: string; status: string; clientSecret: string }>(
      `${this.base}/tenant-portal/me/payments/authorize-card`, request, { withCredentials: true });
  }

  autoPayStatus(orgId?: string | null, leaseId?: string | null, unitId?: string | null): Observable<TenantAutoPayEnrollment | null> {
    let params = new HttpParams();
    if (orgId?.trim()) params = params.set('orgId', orgId.trim());
    if (leaseId?.trim()) params = params.set('leaseId', leaseId.trim());
    if (unitId?.trim()) params = params.set('unitId', unitId.trim());
    return this.http.get<TenantAutoPayEnrollment | null>(`${this.base}/tenant-portal/me/auto-pay`, {
      params,
      withCredentials: true,
    });
  }

  createAutoPaySetupIntent(
    orgId: string,
    leaseId: string,
    unitId: string | null | undefined,
    request: TenantAutoPaySetupIntentRequest,
  ): Observable<TenantAutoPaySetupIntentResponse> {
    let params = new HttpParams().set('orgId', orgId).set('leaseId', leaseId);
    if (unitId?.trim()) params = params.set('unitId', unitId.trim());
    return this.http.post<TenantAutoPaySetupIntentResponse>(`${this.base}/tenant-portal/me/auto-pay/setup-intent`, request, {
      params,
      withCredentials: true,
    });
  }

  completeAutoPaySetup(
    orgId: string,
    leaseId: string,
    unitId: string | null | undefined,
    request: TenantAutoPayCompleteSetupRequest,
  ): Observable<TenantAutoPayEnrollment> {
    let params = new HttpParams().set('orgId', orgId).set('leaseId', leaseId);
    if (unitId?.trim()) params = params.set('unitId', unitId.trim());
    return this.http.post<TenantAutoPayEnrollment>(`${this.base}/tenant-portal/me/auto-pay/complete-setup`, request, {
      params,
      withCredentials: true,
    });
  }

  enrollPlatformAutoPay(
    orgId: string,
    leaseId: string,
    unitId: string | null | undefined,
    request: TenantAutoPayPlatformEnrollRequest,
  ): Observable<TenantAutoPayEnrollment> {
    let params = new HttpParams().set('orgId', orgId).set('leaseId', leaseId);
    if (unitId?.trim()) params = params.set('unitId', unitId.trim());
    return this.http.post<TenantAutoPayEnrollment>(`${this.base}/tenant-portal/me/auto-pay/platform-enroll`, request, {
      params,
      withCredentials: true,
    });
  }

  updateAutoPay(
    orgId: string,
    leaseId: string,
    unitId: string | null | undefined,
    request: TenantAutoPayUpdateRequest,
  ): Observable<TenantAutoPayEnrollment> {
    let params = new HttpParams().set('orgId', orgId).set('leaseId', leaseId);
    if (unitId?.trim()) params = params.set('unitId', unitId.trim());
    return this.http.patch<TenantAutoPayEnrollment>(`${this.base}/tenant-portal/me/auto-pay`, request, {
      params,
      withCredentials: true,
    });
  }

  submitPaymentProof(formData: FormData): Observable<TenantPaymentProof> {
    return this.http.post<TenantPaymentProof>(`${this.base}/tenant-portal/me/payment-proofs`, formData, {
      withCredentials: true,
    });
  }

  paymentProofs(orgId: string, leaseId?: string | null): Observable<TenantPage<TenantPaymentProof>> {
    let params = new HttpParams()
      .set('orgId', orgId)
      .set('page', '0')
      .set('size', '20');

    if (leaseId?.trim()) {
      params = params.set('leaseId', leaseId.trim());
    }

    return this.http.get<TenantPage<TenantPaymentProof>>(`${this.base}/tenant-portal/me/payment-proofs`, {
      params,
      withCredentials: true,
    });
  }

  private documentFormData(request: {
    orgId: string;
    leaseId?: string | null;
    unitId?: string | null;
    title?: string | null;
    description?: string | null;
    category?: string | null;
    file?: File | null;
    files?: File[] | null;
  }): FormData {
    const form = new FormData();
    form.append('orgId', request.orgId);
    if (request.leaseId?.trim()) {
      form.append('leaseId', request.leaseId.trim());
    }
    if (request.unitId?.trim()) {
      form.append('unitId', request.unitId.trim());
    }
    if (request.title?.trim()) {
      form.append('title', request.title.trim());
    }
    if (request.description?.trim()) {
      form.append('description', request.description.trim());
    }
    if (request.category?.trim()) {
      form.append('category', request.category.trim());
    }
    const files = (request.files ?? []).filter(Boolean);
    if (files.length) {
      files.forEach((file) => form.append('files', file, file.name));
      form.append('file', files[0], files[0].name);
    } else if (request.file) {
      form.append('file', request.file, request.file.name);
    }
    return form;
  }
}

export type TenantRenewalRequestStatus = 'OPEN' | 'OFFERED' | 'ACCEPTED' | 'DECLINED' | 'WITHDRAWN' | 'EXPIRED' | 'OFFER_DECLINED';

export interface TenantRenewalRequest {
  id: string;
  leaseId: string;
  status: TenantRenewalRequestStatus;
  requestedTermMonths: number;
  requestedStartDate: string;
  requestedEndDate: string;
  note?: string | null;
  declineReason?: string | null;
  requestedAt?: string | null;
  respondedAt?: string | null;
  renewalLeaseId?: string | null;
}

/** A renewal offer waiting for the tenant: they sign it the usual way, or decline it. */
export interface TenantRenewalOffer {
  renewalLeaseId: string;
  startDate: string;
  endDate: string;
  rent: number | null;
  expiresOn: string | null;
}

export interface TenantDepositStatement {
  id: string;
  status: 'SENT' | 'REFUNDED' | 'CLOSED';
  currency: string;
  depositHeld: number;
  moveOutDate: string;
  dueDate: string;
  totalDeductions: number;
  refundDue: number;
  tenantOwes: number;
  refundedOn: string | null;
  refundMethod: string | null;
  deductions: { id: string; category: 'UNPAID_CHARGE' | 'DAMAGE' | 'CLEANING' | 'OTHER'; description: string; amount: number; paidFromDeposit: number | null }[];
}

export interface TenantRenewalStatus {
  canRequest: boolean;
  unavailableReason: string | null;
  leaseEndsOn: string | null;
  windowOpensOn: string | null;
  termOptionsMonths: number[];
  renewalStartsOn: string | null;
  latestRequest: TenantRenewalRequest | null;
  offer: TenantRenewalOffer | null;
}
